/**
 * 冰梭文件传输 JavaScript SDK
 *
 * 提供以下传输能力：
 * - P2P 直传：点对点传输，无需服务器中转，适合内网/可打通NAT的场景
 * - 安全中转：文件经云端中转存储，支持离线传输和多次下载
 * - 分块直传：文件分块直连对象存储（文件库/项目/收件箱），不经过应用服务器
 *
 * 使用者根据自身需求选择模式，SDK 不做自动降级
 */

import { Auth } from './auth.js';
import { HttpClient } from './http.js';
import { SignalingClient } from './signaling.js';
import { WebRTCConnection } from './webrtc.js';
import { FileTransfer } from './file-transfer.js';
import { DirectUploader, sha256Hex } from './direct-uploader.js';

export { DirectUploader, sha256Hex };

/**
 * SDK 配置
 * @typedef {Object} IceShuttleConfig
 * @property {string} appKey - 应用 Key
 * @property {string} appSecret - 应用 Secret
 * @property {string} [serverDomain] - 服务器地址，默认 https://www.wringcloud.com
 * @property {boolean} [debug] - 是否开启调试日志
 * @property {boolean} [useSM3] - 是否使用SM3国密算法，默认 true
 */

/**
 * 传输选项
 * @typedef {Object} TransferOptions
 * @property {string} fileName - 文件名
 * @property {number} fileSize - 文件大小（字节）
 * @property {string} [password] - 传输密码（可选）
 * @property {number} [maxReceiveCount] - 最大接收次数（可选）
 */

/**
 * 传输回调
 * @typedef {Object} TransferCallbacks
 * @property {function(number): void} [onProgress] - 进度回调，参数为百分比(0-100)
 * @property {function(Object): void} [onComplete] - 完成回调
 * @property {function(string): void} [onError] - 错误回调
 * @property {function(Object): void} [onProcessing] - 处理中回调
 */

/**
 * 冰梭 SDK 主类
 */
export class IceShuttle {
  /**
   * @param {IceShuttleConfig} config
   */
  constructor(config) {
    if (!config.appKey || !config.appSecret) {
      throw new Error('appKey 和 appSecret 不能为空');
    }

    this.config = {
      serverDomain: 'https://www.wringcloud.com',
      debug: false,
      useSM3: true,  // 默认启用SM3国密算法
      ...config
    };

    // 传递算法配置给 Auth 类
    this.auth = new Auth(this.config.appKey, this.config.appSecret, { useSM3: this.config.useSM3 });
    this.http = new HttpClient(this.config.serverDomain, this.auth, this.config.debug);
  }

  // ==================== 通用接收（自动判断传输模式） ====================

  /**
   * 自动判断传输类型并接收文件
   * SDK 会先查询任务类型，然后调用对应的接收方法
   *
   * @param {string} transferId - 传输 ID
   * @param {string} token - 接收令牌
   * @returns {Promise<UniversalReceiver>}
   *
   * @example
   * const receiver = await client.receive(transferId, token);
   * console.log('传输模式:', receiver.getTransferMode());
   * await receiver.receiveFile({ onProgress: (p) => console.log(p + '%') });
   */
  async receive(transferId, token) {
    // 查询统一的传输信息接口（包含完整详细参数）
    const info = await this.http.get(`/api/open/transfer/${transferId}/info?token=${encodeURIComponent(token)}`);

    if (!info.transferMode) {
      throw new Error('无法识别传输类型');
    }

    // 根据传输模式创建对应的接收器，使用统一接口返回的完整信息
    if (info.transferMode === 'p2p') {
      // P2P 模式：使用统一接口返回的信令URL和ICE配置
      const signaling = new SignalingClient(this.config.serverDomain);
      const signAuth = await this.auth.generateSignAuth(transferId);
      await signaling.connect(signAuth);
      signaling.join(transferId);

      const receiver = new P2PReceiver({
        transferId,
        token,
        fileName: info.fileName,
        fileSize: info.fileSize,
        signaling,
        signalingUrl: info.signalingUrl,
        signalingToken: info.signalingToken,
        iceServers: info.iceServers,
        http: this.http,
        serverDomain: this.config.serverDomain
      });

      return new UniversalReceiver('p2p', receiver);
    } else if (info.transferMode === 'relay') {
      // Relay 模式：使用统一接口返回的分块信息
      const receiver = new RelayReceiver({
        transferId,
        token,
        fileName: info.fileName,
        fileSize: info.fileSize,
        totalChunks: info.totalChunks,
        chunkSize: info.chunkSize,
        http: this.http,
        serverDomain: this.config.serverDomain
      });

      return new UniversalReceiver('relay', receiver);
    } else {
      throw new Error('未知的传输模式: ' + info.transferMode);
    }
  }

  // ==================== P2P 直传 ====================

  /**
   * 创建 P2P 发送方
   * 调用后返回 P2PSender 对象，可获取接收链接并发送文件
   *
   * @param {TransferOptions} options - 传输选项
   * @returns {Promise<P2PSender>}
   *
   * @example
   * const sender = await client.createP2PSender({
   *   fileName: file.name,
   *   fileSize: file.size
   * });
   * console.log('接收链接:', sender.getReceiveLink());
   * await sender.sendFile(file, { onProgress: (p) => console.log(p + '%') });
   */
  async createP2PSender(options) {
    // 1. 创建传输任务
    const task = await this.http.post('/api/open/p2p/create', {
      fileName: options.fileName,
      fileSize: options.fileSize,
      password: options.password,
      maxReceiveCount: options.maxReceiveCount
    });

    // 2. 连接信令服务器
    const signaling = new SignalingClient(this.config.serverDomain);
    const signAuth = await this.auth.generateSignAuth(task.transferId);
    await signaling.connect(signAuth);

    // 3. 注册传输房间
    signaling.register(task.transferId, options.fileName, options.fileSize);

    return new P2PSender({
      transferId: task.transferId,
      receiveLink: task.receiveLink,
      signaling,
      http: this.http,
      serverDomain: this.config.serverDomain
    });
  }

  /**
   * 创建 P2P 接收方
   * 通过接收链接中的 transferId 和 token 接收文件
   *
   * @param {string} transferId - 传输 ID
   * @param {string} token - 接收令牌
   * @returns {Promise<P2PReceiver>}
   *
   * @example
   * const receiver = await client.createP2PReceiver(transferId, token);
   * await receiver.receiveFile({
   *   onProgress: (p) => console.log(p + '%'),
   *   onComplete: ({ blob, fileName }) => { ... }
   * });
   */
  async createP2PReceiver(transferId, token) {
    // 1. 查询传输任务信息
    const taskInfo = await this.http.get(`/api/open/p2p/${transferId}/info?token=${encodeURIComponent(token)}`);

    // 2. 连接信令服务器
    const signaling = new SignalingClient(this.config.serverDomain);
    const signAuth = await this.auth.generateSignAuth(transferId);
    await signaling.connect(signAuth);

    // 3. 加入传输房间
    signaling.join(transferId);

    return new P2PReceiver({
      transferId,
      token,
      fileName: taskInfo.fileName,
      fileSize: taskInfo.fileSize,
      signaling,
      http: this.http,
      serverDomain: this.config.serverDomain
    });
  }

  // ==================== 安全中转 ====================

  /**
   * 创建安全中转发送方
   * 文件经云端中转存储，支持离线传输和多次下载
   *
   * @param {TransferOptions} options - 传输选项
   * @returns {Promise<RelaySender>}
   *
   * @example
   * const sender = await client.createRelaySender({
   *   fileName: file.name,
   *   fileSize: file.size
   * });
   * console.log('下载链接:', sender.getDownloadLink());
   * await sender.sendFile(file, { onProgress: (p) => console.log(p + '%') });
   */
  async createRelaySender(options) {
    // 1. 创建中转任务（create 接口已包含分块初始化）
    const task = await this.http.post('/api/open/relay/create', {
      fileName: options.fileName,
      fileSize: options.fileSize,
      password: options.password,
      maxReceiveCount: options.maxReceiveCount
    });

    return new RelaySender({
      transferId: task.transferId,
      downloadLink: task.downloadLink,
      totalChunks: task.totalChunks,
      firstChunkSize: task.firstChunkSize,
      chunkSize: task.chunkSize,
      http: this.http,
      serverDomain: this.config.serverDomain
    });
  }

  /**
   * 创建安全中转接收方
   * 通过下载链接中的 transferId 和 token 下载文件
   *
   * @param {string} transferId - 传输 ID
   * @param {string} token - 下载令牌
   * @returns {Promise<RelayReceiver>}
   *
   * @example
   * const receiver = await client.createRelayReceiver(transferId, token);
   * const blob = await receiver.receiveFileAsBlob({
   *   onProgress: (p) => console.log(p + '%')
   * });
   */
  async createRelayReceiver(transferId, token) {
    // 1. 获取任务信息
    const taskInfo = await this.http.get(`/api/open/relay/${transferId}/info?token=${encodeURIComponent(token)}`);

    return new RelayReceiver({
      transferId,
      token,
      fileName: taskInfo.fileName,
      fileSize: taskInfo.fileSize,
      totalChunks: taskInfo.totalChunks,
      chunkSize: taskInfo.chunkSize,
      http: this.http,
      serverDomain: this.config.serverDomain
    });
  }

  // ==================== 通用接口 ====================

  /**
   * 查询 P2P 传输状态
   * @param {string} transferId
   * @returns {Promise<Object>}
   */
  async getP2PTransferStatus(transferId) {
    return this.http.get(`/api/open/p2p/${transferId}/status`);
  }

  /**
   * 查询安全中转传输状态
   * @param {string} transferId
   * @returns {Promise<Object>}
   */
  async getRelayTransferStatus(transferId) {
    return this.http.get(`/api/open/relay/${transferId}/status`);
  }

  /**
   * 取消 P2P 传输
   * @param {string} transferId
   * @returns {Promise<void>}
   */
  async cancelP2PTransfer(transferId) {
    return this.http.post(`/api/open/p2p/${transferId}/cancel`);
  }

  /**
   * 取消安全中转传输
   * @param {string} transferId
   * @returns {Promise<void>}
   */
  async cancelRelayTransfer(transferId) {
    return this.http.post(`/api/open/relay/${transferId}/cancel`);
  }

  // ==================== 传输域扩展（V3.11.0） ====================

  /**
   * 查询传输策略
   * 返回当前权益下可用的传输模式与限额，供创建任务前预判
   * @returns {Promise<Object>} { level, canP2P, canOssRelay, canOffline, maxFileSize, transferRemaining, memberTrafficRemainingBytes, bonusTrafficRemainingBytes, recommendedMode }
   */
  async getTransferStrategy() {
    return this.http.get('/api/open/transfer/strategy');
  }

  /**
   * 查询传输任务列表（创建者视角，供对账）
   * @param {Object} [options] - 查询选项
   * @param {number} [options.page=1] - 页码
   * @param {number} [options.size=20] - 每页条数（1-100）
   * @param {number} [options.status] - 任务状态筛选（1=传输中 2=已完成 3=已取消 4=已完结）
   * @param {string} [options.transferMode] - 传输模式筛选（p2p / oss_relay）
   * @param {boolean} [options.isOffline] - 是否离线传输筛选
   * @returns {Promise<Object>} { records, total, page, size, pages }
   */
  async listTransfers(options = {}) {
    const params = new URLSearchParams();
    if (options.page != null) params.set('page', options.page);
    if (options.size != null) params.set('size', options.size);
    if (options.status != null) params.set('status', options.status);
    if (options.transferMode) params.set('transferMode', options.transferMode);
    if (options.isOffline != null) params.set('isOffline', options.isOffline);
    const qs = params.toString();
    return this.http.get(`/api/open/transfer/list${qs ? '?' + qs : ''}`);
  }

  /**
   * 获取安全中转任务的整文件下载地址（仅已完成的传输任务）
   * 返回短时下载链接（5 分钟有效），链接被实际访问时按整文件大小扣发送方下载流量
   * @param {string} transferId - 传输 ID
   * @param {string} token - 发送令牌或接收令牌
   * @returns {Promise<Object>} { transferId, fileName, fileSize, downloadUrl, expiresInSeconds, billingNote }
   */
  async getTransferDownloadUrl(transferId, token) {
    return this.http.get(`/api/open/transfer/${transferId}/download-url?token=${encodeURIComponent(token)}`);
  }

  // ==================== 交付域（V3.11.0） ====================

  /**
   * 创建交付链接
   * @param {Object} options - 创建选项
   * @param {number} options.projectId - 项目 ID（必填）
   * @param {string} [options.scope='files'] - 交付范围：files=勾选文件 / project=整项目快照
   * @param {number[]} [options.taskIds] - 文件任务 ID 列表（scope=files 时必填）
   * @param {string} options.password - 提取密码（必填）
   * @param {number} [options.expireDays=7] - 有效期天数
   * @param {number} [options.maxDownloads=0] - 下载次数上限（0=不限）
   * @param {string} [options.note] - 交付说明
   * @returns {Promise<Object>} 交付链接信息
   */
  async createDelivery(options) {
    return this.http.post('/api/open/delivery/create', {
      projectId: options.projectId,
      scope: options.scope,
      taskIds: options.taskIds,
      password: options.password,
      expireDays: options.expireDays,
      maxDownloads: options.maxDownloads,
      note: options.note
    });
  }

  /**
   * 查询项目交付链接列表
   * @param {number} projectId - 项目 ID
   * @returns {Promise<Object[]>} 交付链接列表（字段与页面端交付管理一致）
   */
  async listDeliveries(projectId) {
    return this.http.get(`/api/open/delivery/list?projectId=${projectId}`);
  }

  /**
   * 查询交付链接详情（密码不回显，仅 hasPassword）
   * @param {number} linkId - 交付链接 ID
   * @returns {Promise<Object>} 链接详情
   */
  async getDelivery(linkId) {
    return this.http.get(`/api/open/delivery/${linkId}`);
  }

  /**
   * 配置交付链接（均可选，传了才改）
   * @param {number} linkId - 交付链接 ID
   * @param {Object} [options] - 配置项
   * @param {number} [options.expireDays] - 有效期天数
   * @param {number} [options.maxDownloads] - 下载次数上限
   * @param {string} [options.note] - 交付说明
   * @param {string} [options.password] - 提取密码
   * @returns {Promise<void>}
   */
  async configDelivery(linkId, options = {}) {
    return this.http.post(`/api/open/delivery/${linkId}/config`, {
      expireDays: options.expireDays,
      maxDownloads: options.maxDownloads,
      note: options.note,
      password: options.password
    });
  }

  /**
   * 禁用/启用交付链接
   * @param {number} linkId - 交付链接 ID
   * @param {boolean} disabled - true=禁用 false=启用
   * @returns {Promise<void>}
   */
  async setDeliveryStatus(linkId, disabled) {
    return this.http.post(`/api/open/delivery/${linkId}/status`, { disabled });
  }

  /**
   * 查询交付回执列表（谁在什么时候下载了什么）
   * @param {number} linkId - 交付链接 ID
   * @param {Object} [options] - 分页选项
   * @param {number} [options.page=1] - 页码
   * @param {number} [options.size=10] - 每页条数
   * @returns {Promise<Object>} 分页回执列表
   */
  async getDeliveryRecords(linkId, options = {}) {
    const page = options.page != null ? options.page : 1;
    const size = options.size != null ? options.size : 10;
    return this.http.get(`/api/open/delivery/${linkId}/records?page=${page}&size=${size}`);
  }

  /**
   * 获取交付文件的整文件下载地址
   * 返回短时下载链接；密码校验/计费/回执/次数上限在链接被实际访问时完成
   * @param {number} linkId - 交付链接 ID
   * @param {string} password - 提取密码
   * @param {number} fileTaskId - 文件任务 ID
   * @returns {Promise<Object>} { downloadUrl, ... }
   */
  async getDeliveryDownloadUrl(linkId, password, fileTaskId) {
    return this.http.get(`/api/open/delivery/${linkId}/download-url?password=${encodeURIComponent(password)}&fileTaskId=${fileTaskId}`);
  }

  // ==================== 分块直传（文件库 / 项目 / 收件箱） ====================

  /**
   * 创建分块直传会话（低层）
   * 服务端返回分块计划（chunkSize/totalChunks），若同 fileHash 存在未完成会话则自动续传（resumed=true）
   *
   * @param {Object} options
   * @param {string} options.bizType - 业务类型：drive（文件库）/ project（项目）/ collect（收件箱）
   * @param {Object} options.bizRef - 业务定位：
   *   drive:   { folderId? }（省略 = 根目录）
   *   project: { projectId 必填, folderId? }
   *   collect: { token 必填, uploaderName 必填, contact?, company?, password? }
   * @param {string} options.fileName - 文件名
   * @param {number} options.fileSize - 文件字节数
   * @param {string} [options.fileHash] - 文件 SHA-256（十六进制），用于秒传/断点续传，建议传入
   * @param {boolean} [options.accel] - 是否请求传输加速（由服务端按套餐与链接策略裁定）
   * @returns {Promise<Object>} { sessionKey, bizType, fileName, fileSize, chunkSize, totalChunks, uploadedChunks[], isAccel, resumed }
   *
   * @example
   * const session = await client.createUploadSession({
   *   bizType: 'drive', bizRef: { folderId: 0 },
   *   fileName: file.name, fileSize: file.size, fileHash: '...'
   * });
   */
  async createUploadSession(options) {
    return this.http.post('/api/open/upload-sessions', {
      bizType: options.bizType,
      bizRef: options.bizRef,
      fileName: options.fileName,
      fileSize: options.fileSize,
      fileHash: options.fileHash,
      accel: options.accel
    });
  }

  /**
   * 批量签发分块上传凭证（预签名 PUT 直传对象存储，单批最多 64 个）
   * @param {string} sessionKey - 直传会话 Key
   * @param {number[]} chunkIndexes - 分块索引数组（0-based）
   * @param {string} [collectToken] - collect 会话的收件令牌
   * @returns {Promise<Object>} { credentials: [{ chunkIndex, uploadUrl, expiresIn, accel, accelDegraded }] }
   */
  async getUploadCredentials(sessionKey, chunkIndexes, collectToken) {
    const body = { chunkIndexes };
    if (collectToken) body.collectToken = collectToken;
    return this.http.post(`/api/open/upload-sessions/${sessionKey}/credentials`, body);
  }

  /**
   * 分块上传回执（记录进度，作为断点续传依据）
   * @param {string} sessionKey
   * @param {number} chunkIndex
   * @param {string} [collectToken]
   */
  async ackUploadChunk(sessionKey, chunkIndex, collectToken) {
    const body = collectToken ? { collectToken } : {};
    return this.http.post(`/api/open/upload-sessions/${sessionKey}/chunks/${chunkIndex}/ack`, body);
  }

  /**
   * 完成直传会话：对象存储分块合并 + 业务落地（配额校验与计费归因在此发生）
   * drive 返回 { transferId }，project 返回 { fileId, projectId, ... }，collect 返回收件提交记录
   * @param {string} sessionKey
   * @param {string} [collectToken]
   * @returns {Promise<Object>} 落地结果
   */
  async completeUploadSession(sessionKey, collectToken) {
    const body = collectToken ? { collectToken } : {};
    return this.http.post(`/api/open/upload-sessions/${sessionKey}/complete`, body);
  }

  /**
   * 取消直传会话并清理已上传分块
   * @param {string} sessionKey
   * @param {string} [collectToken]
   */
  async abortUploadSession(sessionKey, collectToken) {
    const body = collectToken ? { collectToken } : {};
    return this.http.post(`/api/open/upload-sessions/${sessionKey}/abort`, body);
  }

  /**
   * 创建直传器（低层）：初始化会话并返回 DirectUploader，适合自定义调度
   * @param {Object} options - 见 createUploadSession；collect 会话额外支持 collectToken
   * @returns {Promise<DirectUploader>}
   */
  async createUploader(options) {
    const session = await this.createUploadSession(options);
    return new DirectUploader({ session, http: this.http, client: this, collectToken: options.collectToken });
  }

  /**
   * 一行代码直传文件（高层封装）：自动计算哈希 → 初始化/续传会话 → 并发分块直传对象存储 → 回执 → 合并落地
   * 文件字节不经过应用服务器，直连对象存储
   *
   * @param {Blob|File} file - 要上传的文件
   * @param {Object} options
   * @param {string} options.bizType - 业务类型：drive / project / collect
   * @param {Object} options.bizRef - 业务定位（见 createUploadSession）
   * @param {string} [options.fileName] - 覆盖文件名（默认取 file.name）
   * @param {string} [options.fileHash] - 文件 SHA-256（十六进制）；不传则自动计算（需 Web Crypto 环境）
   * @param {boolean} [options.accel] - 是否请求传输加速
   * @param {string} [options.collectToken] - collect 会话的收件令牌
   * @param {number} [options.concurrency=3] - 并发上传分块数（1-8）
   * @param {Function} [options.onProgress] - 进度回调 (percent, { uploadedBytes, totalBytes })
   * @param {Function} [options.onSession] - 会话建立回调 ({ sessionKey, totalChunks, chunkSize, resumed })
   * @returns {Promise<Object>} 落地结果（drive: { transferId }，project: { fileId }，collect: 提交记录）
   *
   * @example
   * // 上传到文件库根目录
   * const result = await client.uploadFile(file, {
   *   bizType: 'drive',
   *   bizRef: { folderId: 0 },
   *   onProgress: (percent) => console.log(percent + '%')
   * });
   * console.log('文件库 transferId:', result.transferId);
   */
  async uploadFile(file, options = {}) {
    if (!file) throw new Error('file 不能为空');
    const fileName = options.fileName || file.name || 'unnamed';
    const fileSize = file.size;
    const fileHash = options.fileHash || await sha256Hex(file);

    const uploader = await this.createUploader({
      bizType: options.bizType,
      bizRef: options.bizRef,
      fileName,
      fileSize,
      fileHash,
      accel: options.accel,
      collectToken: options.collectToken
    });

    if (options.onSession) {
      options.onSession({
        sessionKey: uploader.sessionKey,
        totalChunks: uploader.totalChunks,
        chunkSize: uploader.chunkSize,
        resumed: uploader.resumed
      });
    }

    await uploader.upload(file, options);
    return this.completeUploadSession(uploader.sessionKey, options.collectToken);
  }
}

// ==================== P2P 发送方 ====================

/**
 * P2P 发送方
 * 通过 WebRTC 直连传输文件，发送方需保持页面打开
 */
export class P2PSender {
  constructor({ transferId, receiveLink, signaling, http, serverDomain }) {
    this.transferId = transferId;
    this._receiveLink = receiveLink;
    this._signaling = signaling;
    this._http = http;
    this._serverDomain = serverDomain;
    this._webrtc = null;
    this._fileTransfer = null;
    this._canceled = false;
  }

  /**
   * 获取接收链接，分享给接收方
   * @returns {string}
   */
  getReceiveLink() {
    return this._receiveLink;
  }

  /**
   * 发送文件
   * @param {File} file - 要发送的文件
   * @param {TransferCallbacks} callbacks - 回调
   */
  async sendFile(file, callbacks = {}) {
    if (this._canceled) throw new Error('传输已取消');

    try {
      const iceServers = await this._getIceServers();

      // 初始化 WebRTC
      this._webrtc = new WebRTCConnection({
        isInitiator: true,
        transferId: this.transferId,
        signaling: this._signaling,
        iceServers,
        onDataChannelOpen: () => {
          if (this._fileTransfer) {
            this._fileTransfer.startSend().catch(e => {
              callbacks.onError && callbacks.onError(e.message);
              // 传输失败时自动取消，释放权益
              this.cancel().catch(() => {});
            });
          }
        },
        onConnectionStateChange: (state) => {
          if (state === 'disconnected' || state === 'failed') {
            callbacks.onError && callbacks.onError('P2P 连接已断开');
            // 连接断开时自动取消，释放权益
            this.cancel().catch(() => {});
          }
        }
      });

      // 初始化文件传输
      this._fileTransfer = new FileTransfer({
        webrtc: this._webrtc,
        onProgress: (percent) => callbacks.onProgress && callbacks.onProgress(percent),
        onComplete: (info) => {
          this._signaling.sendComplete(this.transferId);
          callbacks.onComplete && callbacks.onComplete(info);
        },
        onError: (msg) => {
          callbacks.onError && callbacks.onError(msg);
          // 传输失败时自动取消，释放权益
          this.cancel().catch(() => {});
        },
        onProcessing: (info) => callbacks.onProcessing && callbacks.onProcessing(info)
      });
      this._fileTransfer.setFile(file);

      await this._webrtc.init();

      // 监听接收方加入
      this._signaling.on('peer_joined', async () => {
        // 通知接收方使用 P2P 模式
        this._signaling.sendTransferMode(this.transferId, 'p2p');
        // 创建 Offer
        const offer = await this._webrtc.createOffer();
        this._signaling.sendOffer(this.transferId, offer.sdp);
      });

      this._signaling.on('answer', (data) => {
        if (this._webrtc) this._webrtc.setRemoteAnswer(data.sdp);
      });

      this._signaling.on('candidate', (data) => {
        if (this._webrtc && data.candidate) this._webrtc.addIceCandidate(data.candidate);
      });
    } catch (e) {
      callbacks.onError && callbacks.onError(e.message || 'P2P 传输失败');
      // 异常时自动取消，释放权益
      await this.cancel();
      throw e;
    }
  }

  /**
   * 取消传输
   */
  async cancel() {
    this._canceled = true;
    if (this._fileTransfer) this._fileTransfer.cancel();
    if (this._webrtc) this._webrtc.close();
    if (this._signaling) this._signaling.disconnect();
    try { await this._http.post(`/api/open/p2p/${this.transferId}/cancel`); } catch (e) {}
  }

  async _getIceServers() {
    try {
      const config = await this._http.get('/api/transfer/ice-config');
      if (config && Array.isArray(config)) {
        return config.filter(s => {
          const urls = s.urls || '';
          return typeof urls === 'string' ? urls.includes('stun') : urls.some(u => u.includes('stun'));
        });
      }
    } catch (e) {}
    return [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }];
  }
}

// ==================== P2P 接收方 ====================

/**
 * P2P 接收方
 * 通过 WebRTC 直连接收文件
 */
export class P2PReceiver {
  constructor({ transferId, token, fileName, fileSize, signaling, signalingUrl, signalingToken, iceServers, http, serverDomain }) {
    this.transferId = transferId;
    this.token = token;
    this.fileName = fileName;
    this.fileSize = fileSize;
    this._signaling = signaling;
    this._signalingUrl = signalingUrl;
    this._signalingToken = signalingToken;
    this._iceServers = iceServers;
    this._http = http;
    this._serverDomain = serverDomain;
    this._webrtc = null;
    this._fileTransfer = null;
  }

  /**
   * 获取文件名
   * @returns {string}
   */
  getFileName() {
    return this.fileName;
  }

  /**
   * 接收文件
   * @param {TransferCallbacks} callbacks - 回调，onComplete 参数为 { blob, fileName, fileSize }
   */
  async receiveFile(callbacks = {}) {
    // 使用传入的 ICE 配置，如果没有则使用默认值
    const iceServers = this._iceServers || await this._getDefaultIceServers();

    this._fileTransfer = new FileTransfer({
      onProgress: (percent) => callbacks.onProgress && callbacks.onProgress(percent),
      onComplete: (info) => {
        callbacks.onComplete && callbacks.onComplete({
          blob: info.blob,
          fileName: info.fileName || this.fileName,
          fileSize: info.fileSize || this.fileSize
        });
      },
      onError: (msg) => callbacks.onError && callbacks.onError(msg),
      onProcessing: (info) => callbacks.onProcessing && callbacks.onProcessing(info)
    });

    this._webrtc = new WebRTCConnection({
      isInitiator: false,
      transferId: this.transferId,
      signaling: this._signaling,
      iceServers,
      onDataChannelOpen: () => {},
      onControlMessage: (msg) => {
        if (this._fileTransfer) this._fileTransfer.handleControlMessage(msg);
      },
      onDataMessage: (data) => {
        if (this._fileTransfer) this._fileTransfer.handleReceive(data);
      },
      onConnectionStateChange: (state) => {
        if (state === 'disconnected' || state === 'failed') {
          callbacks.onError && callbacks.onError('P2P 连接已断开');
        }
      }
    });

    await this._webrtc.init();

    // 监听信令事件
    this._signaling.on('offer', async (data) => {
      const answer = await this._webrtc.createAnswer(data.sdp);
      this._signaling.sendAnswer(this.transferId, answer.sdp);
    });

    this._signaling.on('candidate', (data) => {
      if (this._webrtc && data.candidate) this._webrtc.addIceCandidate(data.candidate);
    });
  }

  async _getDefaultIceServers() {
    return [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }];
  }
}

// ==================== 安全中转发送方 ====================

/**
 * 安全中转发送方
 * 文件经云端中转存储，支持离线传输和多次下载
 */
export class RelaySender {
  constructor({ transferId, downloadLink, totalChunks, firstChunkSize, chunkSize, http, serverDomain }) {
    this.transferId = transferId;
    this._downloadLink = downloadLink;
    this._totalChunks = totalChunks;
    this._firstChunkSize = firstChunkSize;
    this._chunkSize = chunkSize;
    this._http = http;
    this._serverDomain = serverDomain;
    this._canceled = false;
  }

  /**
   * 获取下载链接，分享给接收方
   * @returns {string}
   */
  getDownloadLink() {
    return this._downloadLink;
  }

  /**
   * 发送文件（上传到中转服务器）
   * @param {File} file - 要发送的文件
   * @param {TransferCallbacks} callbacks - 回调
   */
  async sendFile(file, callbacks = {}) {
    if (this._canceled) throw new Error('传输已取消');

    try {
      // 使用 create 返回的分块信息
      const totalChunks = this._totalChunks;
      const firstChunkSize = this._firstChunkSize;
      const chunkSize = this._chunkSize;
      const fileSize = file.size;
      let uploadedBytes = 0;

      // 逐块上传
      for (let i = 0; i < totalChunks; i++) {
        if (this._canceled) throw new Error('传输已取消');

        // 计算分块偏移和大小
        const offset = i === 0 ? 0 : firstChunkSize + (i - 1) * chunkSize;
        const currentChunkSize = i === 0
          ? Math.min(firstChunkSize, fileSize)
          : Math.min(chunkSize, Math.max(0, fileSize - offset));

        if (currentChunkSize <= 0) break;

        const chunkBlob = file.slice(offset, offset + currentChunkSize);

        // 获取上传凭证（修正接口路径）
        const cred = await this._http.post(`/api/open/relay/${this.transferId}/chunk-upload-url`, {
          chunkIndex: i
        });

        // 上传分块到 OSS
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const totalProgress = Math.round(((uploadedBytes + e.loaded) / fileSize) * 100);
              callbacks.onProgress && callbacks.onProgress(totalProgress);
            }
          };
          xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error('分块上传失败'));
          xhr.onerror = () => reject(new Error('分块上传网络错误'));
          xhr.open('PUT', cred.uploadUrl, true);
          xhr.send(chunkBlob);
        });

        uploadedBytes += currentChunkSize;

        // 通知服务器分块已上传（修正接口路径）
        await this._http.post(`/api/open/relay/${this.transferId}/chunk-uploaded`, {
          chunkIndex: i
        });
      }

      // 通知服务器上传完成（修正接口路径）
      await this._http.post(`/api/open/relay/${this.transferId}/complete`);

      callbacks.onComplete && callbacks.onComplete({
        downloadLink: this._downloadLink
      });

    } catch (e) {
      callbacks.onError && callbacks.onError(e.message || '上传失败');
      // 传输失败时自动取消，释放权益
      await this.cancel();
      throw e;
    }
  }

  /**
   * 取消传输
   */
  async cancel() {
    this._canceled = true;
    try { await this._http.post(`/api/open/relay/${this.transferId}/cancel`); } catch (e) {}
  }
}

// ==================== 安全中转接收方 ====================

/**
 * 安全中转接收方
 * 从中转服务器下载文件，无需发送方在线
 */
export class RelayReceiver {
  constructor({ transferId, token, fileName, fileSize, totalChunks, chunkSize, http, serverDomain }) {
    this.transferId = transferId;
    this.token = token;
    this.fileName = fileName;
    this.fileSize = fileSize;
    this._totalChunks = totalChunks;
    this._chunkSize = chunkSize;
    this._http = http;
    this._serverDomain = serverDomain;
  }

  /**
   * 获取文件名
   * @returns {string}
   */
  getFileName() {
    return this.fileName;
  }

  /**
   * 接收文件（下载为 Blob）
   * @param {TransferCallbacks} callbacks - 回调，onComplete 不会被调用，直接返回 Blob
   * @returns {Promise<Blob>}
   */
  async receiveFileAsBlob(callbacks = {}) {
    const chunks = new Array(this._totalChunks).fill(null);
    let downloadedBytes = 0;

    for (let i = 0; i < this._totalChunks; i++) {
      // 获取分块下载凭证（修正接口路径，使用 GET 请求）
      const cred = await this._http.get(`/api/open/relay/${this.transferId}/chunk-download-url?chunkIndex=${i}&token=${encodeURIComponent(this.token)}`);

      // 下载分块
      const response = await fetch(cred.downloadUrl);
      const buffer = await response.arrayBuffer();
      chunks[i] = buffer;
      downloadedBytes += buffer.byteLength;

      const percent = Math.round((downloadedBytes / this.fileSize) * 100);
      callbacks.onProgress && callbacks.onProgress(percent);
    }

    // 合并分块
    const blob = new Blob(chunks, { type: 'application/octet-stream' });
    callbacks.onComplete && callbacks.onComplete({ blob, fileName: this.fileName, fileSize: this.fileSize });
    return blob;
  }
}

/**
 * 触发浏览器下载
 * @param {Blob} blob - 文件 Blob
 * @param {string} fileName - 文件名
 */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ==================== 通用接收器 ====================

/**
 * 通用接收器
 * 统一封装 P2P 和 Relay 接收，提供一致的接口
 */
export class UniversalReceiver {
  constructor(mode, receiver) {
    this._mode = mode;
    this._receiver = receiver;
  }

  /**
   * 获取传输模式
   * @returns {string} 'p2p' 或 'relay'
   */
  getTransferMode() {
    return this._mode;
  }

  /**
   * 获取文件名
   * @returns {string}
   */
  getFileName() {
    return this._receiver.getFileName();
  }

  /**
   * 接收文件
   * @param {TransferCallbacks} callbacks - 回调
   * - P2P 模式：onComplete 参数为 { blob, fileName, fileSize }
   * - Relay 模式：返回 Blob，onComplete 也会被调用
   * @returns {Promise<Blob|void>} Relay 模式返回 Blob，P2P 模式无返回值
   */
  async receiveFile(callbacks = {}) {
    if (this._mode === 'relay') {
      // Relay 模式返回 Blob
      return this._receiver.receiveFileAsBlob(callbacks);
    } else {
      // P2P 模式无返回值，通过 onComplete 回调获取 blob
      return this._receiver.receiveFile(callbacks);
    }
  }
}
